/* eslint-disable @typescript-eslint/no-explicit-any */
import { MoreOutlined } from '@ant-design/icons'
import { SorterResult } from 'antd/es/table/interface'
import {
  IParamsGetTradeUnionMember,
  ITradeUnionMember,
} from 'app/api/tradeUnionMembers/model'
import R from 'app/assets/R'
import { ENV_CONFIG } from 'app/common/config'
import { PermissionEnum } from 'app/common/enum'
import { ISort } from 'app/common/interface'
import { BaseButton } from 'app/components/common/BaseButton'
import { BasePopover } from 'app/components/common/BasePopover'
import { BaseSpace } from 'app/components/common/BaseSpace'
import BaseText from 'app/components/common/BaseText'
import { TUM_YOUR_UNIT_MANAGEMENT_DETAILS_PATH } from 'app/components/router/route-path'
import { RenderValueTable } from 'app/components/tables/BaseTableManagement/hook'
import { convertColumnTable } from 'app/components/tables/BaseTableReport/hook'
import { useVerifyPermission } from 'app/helpers/permission.helper'
import { useDebounce, usePagination } from 'app/hook'
import { useGetTradeUnionMember } from 'app/react-query/hook/tradeUnionMember'
import { IResponseQueryList } from 'app/react-query/model/common'
import { handleDownloadExcel } from 'app/service/general/service'
import { Pagination, initialPagination } from 'parkway-web-common'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { DeleteModalLayout, UpdateTumModal } from './components'
import { IDataTable } from './type'

export const useHook = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, verifyPermissionAndRedirect, verifyPermission } =
    useVerifyPermission()

  useEffect(() => {
    if (!user?.member?.unit?.id) {
      navigate(-1)
      return
    }
    verifyPermissionAndRedirect(PermissionEnum.ReadTradeUnionMemberInYourUnit)
  }, [user])

  const [pagination, setPagination] = useState<Pagination>(initialPagination)
  const [filter, setFilter] = useState<IParamsGetTradeUnionMember>({})

  const filterDebounce = useDebounce(filter, 500)
  const [sorts, setSort] = useState<ISort[]>([])

  const { flattenDataList } = usePagination()
  const { data: dataApi, isLoading } = useGetTradeUnionMember({
    search: filterDebounce?.search,
    page: pagination?.current,
    limit: pagination?.pageSize,
    source: filterDebounce?.source,
    sorts,
    activityIds: filterDebounce?.activityIds,
    schoolIds: filterDebounce?.schoolIds,
    unitIds: [user?.member?.unit?.id],
    degreeIds: filterDebounce?.degreeIds,
    tinhTrangLamViec: filterDebounce?.tinhTrangLamViec,
  })

  const flattenData = useMemo(() => {
    const res: IResponseQueryList<ITradeUnionMember[]> =
      flattenDataList(dataApi)

    return res
  }, [dataApi])

  const data: IDataTable[] = useMemo(() => {
    return (
      flattenData?.data?.map((item, index) => ({
        ...item,
        key: index + 1,
      })) ?? []
    )
  }, [flattenData])

  const total = useMemo(() => {
    return flattenData?.total ?? data?.length
  }, [flattenData, data])

  const onChangeFilter = (newFilter: IParamsGetTradeUnionMember) => {
    setFilter(newFilter)
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const isPermissionCreate = useMemo(() => {
    return verifyPermission(PermissionEnum.CreateTradeUnionMemberInYourUnit)
  }, [user])

  const isPermissionUpdate = useMemo(() => {
    return verifyPermission(PermissionEnum.UpdateTradeUnionMemberInYourUnit)
  }, [user])

  const isPermissionDownload = useMemo(() => {
    return verifyPermission(PermissionEnum.ExportTradeUnionMemberInYourUnit)
  }, [user])

  const isPermissionDelete = useMemo(() => {
    return verifyPermission(PermissionEnum.DeleteTradeUnionMemberInYourUnit)
  }, [user])

  const columns = useMemo(() => {
    return [
      convertColumnTable<ITradeUnionMember>({
        key: 'fullName',
        title: t(R.strings.full_name),
        fixed: 'left',
        sorter: true,
        render: (_, record) => {
          return (
            <RenderValueTable
              type="Link"
              value={record?.fullName}
              onClick={() =>
                navigate(TUM_YOUR_UNIT_MANAGEMENT_DETAILS_PATH, {
                  state: {
                    tum: record,
                  },
                })
              }
            />
          )
        },
      }),
      convertColumnTable<ITradeUnionMember>({
        key: 'mql',
        title: t(R.strings.id_management),
        fixed: 'left',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText fontWeight="semibold" children={record?.mql ?? '-'} />
          )
        },
      }),
      convertColumnTable<ITradeUnionMember>({
        key: 'email',
        title: t(R.strings.email),
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.email ?? '-'}
            />
          )
        },
      }),
      convertColumnTable<ITradeUnionMember>({
        key: 'tenDonVi',
        title: 'Đơn vị UEH',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.tenDonVi}
            />
          )
        },
      }),
      convertColumnTable<ITradeUnionMember>({
        key: 'unit',
        title: 'Đơn vị công đoàn',
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.unit?.name ?? '-'}
            />
          )
        },
      }),

      convertColumnTable<ITradeUnionMember>({
        key: 'gioiTinh',
        title: t(R.strings.gender),
        classNameWidthColumnOverwrite: 'medium-column',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.gioiTinh ?? '-'}
            />
          )
        },
      }),
      convertColumnTable<ITradeUnionMember>({
        key: 'nguyenQuan',
        title: t(R.strings.date_of_union_entry),
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.ngayVaoCongDoan ?? '-'}
            />
          )
        },
      }),
      convertColumnTable<ITradeUnionMember>({
        key: 'activityEngagement',
        title: t(R.strings.status_union_activities),
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.activityEngagement?.name ?? '-'}
            />
          )
        },
      }),

      convertColumnTable<ITradeUnionMember>({
        key: 'school',
        title: 'Công đoàn bộ phận',
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.school?.name ?? '-'}
            />
          )
        },
      }),

      convertColumnTable<ITradeUnionMember>({
        key: 'dienThoaiDiDong',
        title: t(R.strings.phone),
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.dienThoaiDiDong ?? '-'}
            />
          )
        },
      }),

      convertColumnTable<ITradeUnionMember>({
        key: 'ngaySinh',
        title: t(R.strings.birthday),
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.ngaySinh ?? '-'}
            />
          )
        },
      }),

      convertColumnTable<ITradeUnionMember>({
        key: 'is_sync',
        title: 'Nguồn dữ liệu',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={record?.is_sync ? 'Từ UEH' : 'Từ hệ thống công đoàn'}
            />
          )
        },
      }),

      convertColumnTable<ITradeUnionMember>({
        key: 'id',
        classNameWidthColumnOverwrite: 'medium-column',
        fixed: 'right',
        render: (_, record) => {
          return (
            <BasePopover
              trigger={'click'}
              content={
                <BaseSpace>
                  {isPermissionUpdate ? <UpdateTumModal tum={record} /> : null}
                  {isPermissionDelete ? (
                    <DeleteModalLayout
                      name={record?.fullName}
                      id={record?.id}
                    />
                  ) : null}
                </BaseSpace>
              }
            >
              <BaseButton icon={<MoreOutlined rev={undefined} />} type="text" />
            </BasePopover>
          )
        },
      }),
    ]
  }, [t])

  const handleChangePagination = (
    pagination: Pagination,
    _,
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => {
    setPagination(pagination)

    let sortParams: { field: string; order: 'ASC' | 'DESC' }[] = []

    if (Array.isArray(sorter)) {
      // Nếu sorter là mảng (multi-sort)
      sortParams = sorter
        .filter(s => s.order) // Lọc ra những cột có thứ tự sắp xếp
        .map(s => ({
          field: s.field as string,
          order: s.order === 'ascend' ? 'ASC' : 'DESC',
        }))
    } else if (sorter.order) {
      // Nếu chỉ có một cột được sort
      sortParams = [
        {
          field: sorter.field as string,
          order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      ]
    }

    setSort(sortParams)
  }

  const downloadExcel = async (): Promise<void> => {
    await handleDownloadExcel({
      params: {
        search: filterDebounce?.search,
        source: filterDebounce?.source,
        sorts,
        activityIds: filterDebounce?.activityIds,
        schoolIds: filterDebounce?.schoolIds,
        unitIds: [user?.member?.unit?.id],
        degreeIds: filterDebounce?.degreeIds,
        tinhTrangLamViec: filterDebounce?.tinhTrangLamViec,
      },
      urlMethod: `${ENV_CONFIG.API_ENDPOINT}trade-union-member/download`,
      fileName: 'Đoàn viên công đoàn',
    })
  }

  return {
    columns,
    pagination: {
      ...pagination,
      total,
    },
    onChangeFilter,
    filter,
    isLoading,
    total,
    handleChangePagination,
    data,
    isPermissionCreate,
    downloadExcel,
    isPermissionDownload,
    user,
    t,
  }
}
